import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Layout, Menu, Button, Modal, Typography, Divider, Badge, Tooltip, Avatar } from "antd";
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  SettingOutlined,
  CalendarOutlined,
  ScheduleOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  FieldTimeOutlined,
  GiftOutlined,
  InfoCircleOutlined,
  BankOutlined,
  HomeOutlined,
  MedicineBoxOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  CommentOutlined
} from "@ant-design/icons";
import styles from "./Sidebar.module.css";
import "../styles/design-system.css";
import { useTranslation } from 'react-i18next';
import { useKeycloak } from "@react-keycloak/web";
import FeedbackModal from "../components/FeedbackModal";

// Version info injected at build time by Vite
const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';
const gitCommit = typeof __GIT_COMMIT__ !== 'undefined' ? __GIT_COMMIT__ : 'dev';
const buildDate = typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : new Date().toISOString();

const { Sider } = Layout;
const { Text, Title } = Typography;

const Sidebar = ({ collapsed, onCollapse, isMobile }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { keycloak } = useKeycloak();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFeedbackVisible, setIsFeedbackVisible] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [openKeys, setOpenKeys] = useState(['settings']);

  // Update selected menu item based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === '/viewer') setSelectedKeys(['viewer']);
    else if (path === '/week-plan') setSelectedKeys(['week-plan']);
    else if (path === '/work-status') setSelectedKeys(['work-status']);
    else if (path === '/summary') setSelectedKeys(['summary']);
    else if (path === '/employees') setSelectedKeys(['organisation']);
    else if (path === '/settings/week-template') setSelectedKeys(['week-template']);
    else if (path === '/') setSelectedKeys(['home']);
    else setSelectedKeys([]);
  }, [location.pathname]);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleLogout = () => {
    keycloak.logout();
  };

  const onOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  // Check if user has admin role
  const isAdmin = keycloak.tokenParsed?.realm_access?.roles?.includes('mediadmin');

  const menuItems = [
    {
      key: 'home',
      icon: <HomeOutlined />,
      label: <Link to="/">{t("home.title")}</Link>,
    },
    {
      key: 'viewer',
      icon: <ClockCircleOutlined />,
      label: <Link to="/viewer">{t("scheduleviewer")}</Link>,
    },
    {
      key: 'week-plan',
      icon: <CalendarOutlined />,
      label: <Link to="/week-plan">{t("weekplan")}</Link>,
    },
    {
      key: 'work-status',
      icon: <FieldTimeOutlined />,
      label: <Link to="/work-status">{t("workstatusapplication")}</Link>,
    },
    {
      key: 'summary',
      icon: <BarChartOutlined />,
      label: <Link to="/summary">{t("summary")}</Link>,
    },
    // Only show settings menu for admin users
    ...(isAdmin ? [{
      key: 'settings',
      icon: <SettingOutlined />,
      label: t("settings"),
      children: [
        {
          key: 'week-template',
          icon: <ScheduleOutlined />,
          label: <Link to="/settings/week-template">{t("weektemplate")}</Link>,
        },
        {
          key: 'organisation',
          icon: <BankOutlined />,
          label: <Link to="/employees">{t("organisation")}</Link>,
        },
      ],
    }] : []),
  ];

  const userInfo = keycloak.tokenParsed;
  const userName = userInfo?.preferred_username || userInfo?.name || t("user");
  const userEmail = userInfo?.email || "";

  return (
    <Sider 
      collapsible 
      collapsed={collapsed} 
      trigger={null} 
      width={256} 
      collapsedWidth={80}
      className={`${styles.sidebar} animate-slide-in-left`}
    >


      {/* User Profile Section */}
      {!collapsed && (
        <div className={styles.userProfile}>
          <Avatar 
            size={48} 
            icon={<UserOutlined />} 
            className={styles.userAvatar}
          />
          <div className={styles.userInfo}>
            <Text strong className={styles.userName}>{userName}</Text>
            <Text type="secondary" className={styles.userEmail}>{userEmail}</Text>
          </div>
        </div>
      )}

      {collapsed && (
        <div className={styles.userProfileCollapsed}>
          <Tooltip title={userName} placement="right">
            <Avatar 
              size={40} 
              icon={<UserOutlined />} 
              className={styles.userAvatarCollapsed}
            />
          </Tooltip>
        </div>
      )}

      <Divider className={styles.userDivider} />

      {/* Main Navigation */}
      <div className={styles.navigationWrapper}>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          className={styles.menu}
          items={menuItems}
        />
      </div>

      {/* Bottom Actions */}
      <div className={styles.bottomActions}>
        <div className={styles.actionButtons}>
          {!collapsed ? (
            <>
              <Button
                type="text"
                icon={<CommentOutlined />}
                onClick={() => setIsFeedbackVisible(true)}
                className={styles.actionButton}
                block
              >
                {t("feedback.title")}
              </Button>
              <Button
                type="text"
                icon={<QuestionCircleOutlined />}
                onClick={showModal}
                className={styles.actionButton}
                block
              >
                {t("aboutApp")}
              </Button>
              <Button
                type="text"
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                className={`${styles.actionButton} ${styles.logoutButton}`}
                block
              >
                {t("logout")}
              </Button>
            </>
          ) : (
            <>
              <Tooltip title={t("feedback.title")} placement="right">
                <Button
                  type="text"
                  icon={<CommentOutlined />}
                  onClick={() => setIsFeedbackVisible(true)}
                  className={styles.actionButtonCollapsed}
                />
              </Tooltip>
              <Tooltip title={t("aboutApp")} placement="right">
                <Button
                  type="text"
                  icon={<QuestionCircleOutlined />}
                  onClick={showModal}
                  className={styles.actionButtonCollapsed}
                />
              </Tooltip>
              <Tooltip title={t("logout")} placement="right">
                <Button 
                  type="text" 
                  icon={<LogoutOutlined />} 
                  onClick={handleLogout}
                  className={`${styles.actionButtonCollapsed} ${styles.logoutButton}`}
                />
              </Tooltip>
            </>
          )}
        </div>
      </div>

      {/* Enhanced About Modal */}
      <Modal 
        title={
          <div className={styles.modalHeader}>
            <MedicineBoxOutlined className={styles.modalIcon} />
            <span>{t("aboutMediSTime")}</span>
          </div>
        }
        open={isModalVisible} 
        onOk={handleOk} 
        onCancel={handleCancel}
        className={styles.aboutModal}
        footer={[
          <Button key="ok" type="primary" onClick={handleOk}>
            {t("close")}
          </Button>
        ]}
      >
        <div className={styles.modalContent}>
          <div className={styles.versionInfo}>
            <Badge.Ribbon text={t("latest")} color="green">
              <div className={styles.versionCard}>
                <div style={{ marginBottom: 8 }}>
                  <Text strong>{t("version")}: </Text>
                  <Text code>{appVersion}</Text>
                </div>
                <div style={{ marginBottom: 4 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>Build: </Text>
                  <Text code style={{ fontSize: 12 }}>{gitCommit}</Text>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>{t("buildDate") || "Date"}: </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {new Date(buildDate).toLocaleDateString()}
                  </Text>
                </div>
              </div>
            </Badge.Ribbon>
          </div>
          
          <div className={styles.companyInfo}>
            <Text type="secondary">
              {t("developedBy")} <Text strong>OptivaSoft</Text>
            </Text>
            <br />
            <Text type="secondary">
              © 2026 OptivaSoft. {t("allRightsReserved")}
            </Text>
          </div>
          
          <div className={styles.supportInfo}>
            <Text type="secondary">
              {t("needHelp")} <a href="mailto:support@medistime.de">{t("contactSupport")}</a>
            </Text>
          </div>
        </div>
      </Modal>

      <FeedbackModal
        open={isFeedbackVisible}
        onClose={() => setIsFeedbackVisible(false)}
      />
    </Sider>
  );
};

export default Sidebar;